import { NgxMatFileInputModule } from '@angular-material-components/file-input';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, take } from 'rxjs';
import { LoadingModule } from 'src/app/main/pages/generals/loading/loading.module';
import { ActivityService } from 'src/app/main/pages/services/activity.service';
import { TaxpayerService } from 'src/app/main/pages/services/taxpayer.service';
import { SharedService } from 'src/app/shared/shared.service';

import { ActivityDialogComponent } from '../../../generals/dialog/activity-dialog/activity-dialog.component';
import { TaxPayerProfileDialogComponent } from '../../../generals/dialog/taxpayer-profile-dialog/taxpayer-profile-dialog.component';
import { ActivitySimpleResponse } from '../../../services/microservices/response/activitySimple.response';
import { TaxpayerIdActivityResponse } from '../../../services/microservices/response/taxpayerIdActivity.response';

@Component({
  selector: 'app-taxpayer',
  templateUrl: './taxpayer.component.html',
  styleUrls: ['./taxpayer.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    FlexLayoutModule,
    NgxMatFileInputModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatButtonModule,
    LoadingModule,
    MatDialogModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatRadioModule,
    FormsModule,
  ],
})
export class TaxpayerComponent implements OnInit, OnDestroy {
  readonly dialog = inject(MatDialog);
  private sharedService = inject(SharedService);
  private taxpayerService = inject(TaxpayerService);
  private activityService = inject(ActivityService);
  private _unsubscribeAll!: Subject<any>;
  private _snackBar = inject(MatSnackBar);
  activities: Array<ActivitySimpleResponse> = [];
  taxpayerId = 0;
  signatureId = 0;
  mainActivitySelected = 0;

  ngOnInit(): void {
    this._unsubscribeAll = new Subject<any>();
    this.load();
  }

  private load(): void {
    this.sharedService.signature$.subscribe(signature => {
      if (signature.id > 0 && this.taxpayerId === 0) {
        this.loadTaxpayer(signature.id);
      }
    });
  }

  private loadTaxpayer(signatureId: number): void {
    this.signatureId = signatureId;
    this.taxpayerService
      .getIdActivity(signatureId)
      .pipe(take(1))
      .subscribe({
        next: response => {
          if (response.data) {
            const taxpayerId = response.data.id;
            if (taxpayerId > 0) {
              this.taxpayerId = taxpayerId;
              this.sharedService.setTaxpayerId(taxpayerId);
              this.activities = response.data.activity;
              this.updateTotalActivities();
              this.setMainActivitySelected();
            }
          }
        },
      });
  }

  addTaxpayer(): void {
    this.dialog
      .open(TaxPayerProfileDialogComponent, {
        height: '',
        width: '450px',
        autoFocus: false,
        data: { signatureId: this.signatureId },
      })
      .afterClosed()
      .subscribe((taxpayer: TaxpayerIdActivityResponse) => {
        if (
          typeof taxpayer !== 'undefined' &&
          typeof taxpayer.id !== 'undefined'
        ) {
          this.load();
          if (this.taxpayerId === 0 && taxpayer.id > 0) {
            this.taxpayerId = taxpayer.id;
            this.editActivity(0, -1);
          }
        }
      });
  }

  editActivity(id: number, index: number): void {
    const dialogRef = this.dialog.open(ActivityDialogComponent, {
      height: '',
      width: '',
      autoFocus: false,
      data: { activityId: id, taxpayerId: this.taxpayerId },
    });
    dialogRef.afterClosed().subscribe((result: ActivitySimpleResponse) => {
      if (typeof result !== 'undefined' && typeof result.id !== 'undefined') {
        if (this.activities) {
          if (index > -1) {
            this.activities[index] = result;
          } else {
            this.activities.push(result);
          }
        } else {
          this.activities = [result];
        }
        this.updateTotalActivities();
      }
    });
  }

  switchActive(index: number, $event: MatSlideToggleChange): void {
    let json = {
      id: this.activities[index].id,
      isActive: $event.checked,
    };
    this.activityService.updateIsActive(json).subscribe({
      next: () => {
        this.activities[index].isActive = $event.checked;
      },
      error: () => {
        this.openSnack('Ups');
      },
    });
  }

  deleteActivity(index: number, id: number): void {
    this.activityService.delete(id).subscribe({
      next: () => {
        this.activities.splice(index, 1);
        this.updateTotalActivities();
      },
      error: () => {
        this.openSnack('Ups');
      },
    });
  }

  setMainActivity(id: number, isMainActivity: boolean) {
    this.activityService.updateMainActivity(id, isMainActivity).subscribe({
      next: () => {
        this.load();
      },
      error: () => {
        this.openSnack('Ocurrió un detalle al actualizar la actividad');
      },
    });
  }

  private updateTotalActivities(): void {
    this.sharedService.setTotalActivities(this.activities.length);
  }

  private setMainActivitySelected(): void {
    this.activities.forEach(activity => {
      if (activity.mainActivity) {
        this.mainActivitySelected = activity.id;
      }
    });
  }

  private openSnack(message: string): void {
    this._snackBar.open(message, '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-error'],
      duration: 5000,
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(void 0);
    this._unsubscribeAll.complete();
  }
}
